<script setup lang="ts">
// KeyboardShortcuts — the "?" overlay listing the shortcuts active RIGHT NOW.
//
// Ports legacy `public/js/components/HelpOverlay.js`, which was not carried
// over in the Vue rewrite: the bindings came across (Live's arrow keys, the
// Guide's day paging, the player's space/k/m/a/f, a dialog's Esc) but nothing
// ever listed them, so they were undiscoverable. Everything this needs already
// existed and was unused — `listActiveBindings()` had no callers and
// `KeyBinding.label` was documented as "for the help overlay".
//
// Mounted ONCE at the app shell next to <ToastStack> / <ConfirmDialogHost>, so
// the "?" binding sits at the bottom of the layer stack and any page or dialog
// layer can shadow it.
//
// The list is read on OPEN, never cached: which bindings are active depends on
// the route, on what dialog is up, and on `when()` predicates that read user
// preferences. A snapshot taken at mount would be wrong by the time anyone
// pressed "?".

import { computed, ref } from 'vue';
import Modal from './Modal.vue';
import { listActiveBindings, useKeyBindings, type ActiveBinding } from '@/composables/useKeyBindings';

const open = ref(false);
const rows = ref<ActiveBinding[]>([]);

function show(): void {
    rows.value = listActiveBindings().filter((b) => !!b.label);
    open.value = true;
}

function toggle(): void {
    if (open.value) open.value = false;
    else show();
}

// Two bindings for one key on purpose. "?" is Shift+/ on US/UK layouts, so
// `ev.shiftKey` is true and a binding declared without `shift` will not match
// (`matches()` compares all four modifiers strictly). Other layouts emit "?"
// with no shift at all. Only the first carries a `label`, so the overlay lists
// the shortcut once instead of showing a duplicate row for the same key.
useKeyBindings(
    [
        { key: '?', shift: true, handler: onHelpKey, label: 'Keyboard shortcuts' },
        { key: '?', handler: onHelpKey },
    ],
    { scope: 'Everywhere' },
);

function onHelpKey(ev: KeyboardEvent): void {
    ev.preventDefault();
    toggle();
}

/**
 * Grouped most-specific-context-first, which is the order
 * `listActiveBindings()` already returns (it walks the stack top-down), so a
 * dialog's shortcuts appear above the page's and the page's above "Everywhere".
 */
const groups = computed<Array<{ scope: string; bindings: ActiveBinding[] }>>(() => {
    const out: Array<{ scope: string; bindings: ActiveBinding[] }> = [];
    for (const b of rows.value) {
        const existing = out.find((g) => g.scope === b.scope);
        if (existing) existing.bindings.push(b);
        else out.push({ scope: b.scope, bindings: [b] });
    }
    return out;
});

/** Printable key names. `' '` is the space bar; arrows read better as glyphs. */
const KEY_LABELS: Record<string, string> = {
    ' ': 'Space',
    ArrowUp: '↑',
    ArrowDown: '↓',
    ArrowLeft: '←',
    ArrowRight: '→',
    Escape: 'Esc',
    PageUp: 'Page Up',
    PageDown: 'Page Down',
    Enter: 'Enter',
};

function keyChips(b: ActiveBinding): string[] {
    const chips: string[] = [];
    if (b.meta) chips.push('⌘');
    if (b.ctrl) chips.push('Ctrl');
    if (b.alt) chips.push('Alt');
    // Shift is implied for a key that only exists as a shifted character —
    // rendering "Shift + ?" would be noise, and "?" is what is printed on the
    // key cap the user is looking for.
    if (b.shift && b.key.length > 1) chips.push('Shift');
    chips.push(KEY_LABELS[b.key] ?? (b.key.length === 1 ? b.key.toUpperCase() : b.key));
    return chips;
}

// No empty state on purpose: this component's own "?" binding is labelled and
// unconditional, so it is always in the list while the component is mounted —
// `groups` cannot be empty at open time. An empty-state branch here would be
// UI that can never render, which is the exact thing this batch is removing
// elsewhere.
defineExpose({ show, toggle });
</script>

<template>
    <Modal :open="open" title="Keyboard shortcuts" @update:open="open = $event">
        <div class="shortcuts" data-testid="keyboard-shortcuts">
            <p class="intro">What these keys do on this screen right now.</p>

            <section v-for="group in groups" :key="group.scope" class="group">
                <h3>{{ group.scope }}</h3>
                <dl>
                    <template v-for="b in group.bindings" :key="`${group.scope}:${b.key}`">
                        <dt>
                            <span v-for="(chip, i) in keyChips(b)" :key="i" class="chip-row">
                                <kbd>{{ chip }}</kbd><span v-if="i < keyChips(b).length - 1" class="plus">+</span>
                            </span>
                        </dt>
                        <dd>{{ b.label }}</dd>
                    </template>
                </dl>
            </section>

            <p class="foot">Press <kbd>?</kbd> or <kbd>Esc</kbd> to close.</p>
        </div>
    </Modal>
</template>

<style scoped>
.shortcuts {
    display: flex;
    flex-direction: column;
    gap: var(--space-md);
    min-width: min(28rem, 80vw);
}

.intro,
.foot {
    margin: 0;
    color: var(--color-text-muted);
    font-size: 13px;
}

.group {
    display: flex;
    flex-direction: column;
    gap: var(--space-xs);
}

h3 {
    margin: 0;
    font-size: 12px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--color-text-muted);
}

dl {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: var(--space-2xs) var(--space-md);
    margin: 0;
    align-items: baseline;
}

dt {
    display: flex;
    align-items: center;
    gap: 2px;
    white-space: nowrap;
}

dd {
    margin: 0;
    font-size: 14px;
    color: var(--color-text-primary);
}

.chip-row {
    display: inline-flex;
    align-items: center;
    gap: 2px;
}

.plus {
    color: var(--color-text-muted);
    font-size: 11px;
}

kbd {
    display: inline-block;
    min-width: 1.6em;
    padding: 2px 6px;
    border: 1px solid var(--color-border);
    border-bottom-width: 2px;
    border-radius: var(--radius-sm);
    background: var(--color-bg-tertiary);
    color: var(--color-text-primary);
    font-family: var(--font-mono);
    font-size: 12px;
    text-align: center;
    line-height: 1.4;
}

.foot {
    padding-top: var(--space-xs);
    border-top: 1px solid var(--color-border);
}
</style>
